import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Coins, FileText, ImageIcon, Clapperboard, RefreshCw, AlertTriangle } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Skeleton } from '../ui/skeleton';
import { toast } from 'sonner';
import { BACKEND_URL, reportError } from './adminApi';

const MONTHS_FR = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const formatMonth = (key) => {
  const [y, m] = key.split('-').map(Number);
  return `${MONTHS_FR[m - 1]} ${y}`;
};
const usd = (v) => `${(v || 0).toFixed(2)} $`;
const eur = (v) => `${(v || 0).toFixed(2).replace('.', ',')} €`;

const KEY_LABELS = { universal: 'Clé universelle', 'own-google': 'Clé Google', 'own-openai': 'Clé OpenAI', 'own-anthropic': 'Clé Anthropic', 'fal-direct': 'Compte fal.ai', 'universal-fallback': 'Universelle (repli)' };

function StatTile({ icon: Icon, label, count, cost, testId }) {
  return (
    <div className="rounded-xl border border-border p-4 bg-card" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-muted-foreground">{label}</span>
        <Icon className="h-4 w-4 text-[hsl(var(--primary))]" />
      </div>
      <p className="text-2xl font-['Space_Grotesk'] font-semibold" data-testid={`${testId}-count`}>{count}</p>
      <p className="text-[11px] text-muted-foreground">≈ {usd(cost)}</p>
    </div>
  );
}

/**
 * Admin card: monthly AI generation volumes (texts, images, videos) with estimated cost.
 */
export function AdminAiUsageCard({ adminKey, refreshToken = 0 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/ai-usage?key=${adminKey}&months=6`);
      setData(res.data);
    } catch (err) {
      reportError('suivi des coûts IA', err, () => toast.error('Impossible de charger le suivi des coûts IA'));
    } finally {
      setLoading(false);
    }
  }, [adminKey]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const current = data?.current_month;
  const billing = Object.entries(data?.billing_this_month || {});

  return (
    <Card className="p-5 sm:p-6" data-testid="ai-usage-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[hsl(var(--brand-mint))] flex items-center justify-center"><Coins className="h-5 w-5 text-[hsl(var(--primary))]" /></div>
          <div>
            <h3 className="font-['Space_Grotesk'] font-semibold">Suivi des coûts IA</h3>
            <p className="text-xs text-muted-foreground">Textes, images et vidéos générés par mois, avec coût estimé.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} aria-label="Actualiser" data-testid="ai-usage-refresh"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
      </div>

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="ai-usage-loading">{[0, 1, 2, 3].map((i) => <Skeleton key={`usage-skel-${i}`} className="h-24 rounded-xl" />)}</div>
      ) : !data ? (
        <div className="text-center py-6" data-testid="ai-usage-error">
          <p className="text-sm text-muted-foreground mb-3">Les statistiques n'ont pas pu être chargées.</p>
          <Button size="sm" variant="outline" onClick={load}>Réessayer</Button>
        </div>
      ) : (
        <div className="space-y-5">
          {/* Current month */}
          <div>
            <p className="text-xs font-medium mb-2">Ce mois-ci — {formatMonth(current.month)}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <StatTile icon={FileText} label="Textes (astuces & tutos)" count={current.texts} cost={current.cost_text_usd} testId="ai-usage-texts" />
              <StatTile icon={ImageIcon} label="Images" count={current.images} cost={current.cost_image_usd} testId="ai-usage-images" />
              <StatTile icon={Clapperboard} label="Vidéos" count={current.videos} cost={current.cost_video_usd} testId="ai-usage-videos" />
              <div className="rounded-xl border-2 border-[hsl(var(--primary))]/30 p-4 bg-[hsl(var(--brand-mint))]/30" data-testid="ai-usage-total">
                <span className="text-xs text-muted-foreground">Coût estimé du mois</span>
                <p className="text-2xl font-['Space_Grotesk'] font-semibold text-[hsl(var(--primary))]" data-testid="ai-usage-total-eur">{eur(current.cost_total_eur)}</p>
                <p className="text-[11px] text-muted-foreground">{usd(current.cost_total_usd)} · depuis le début : {usd(data.all_time.cost_total_usd)}</p>
              </div>
            </div>
          </div>

          {/* Billing split + failures */}
          <div className="flex flex-wrap gap-1.5 items-center" data-testid="ai-usage-billing">
            {billing.length === 0 ? (
              <span className="text-[11px] text-muted-foreground">Répartition par clé disponible à partir des prochaines générations.</span>
            ) : billing.map(([key, v]) => (
              <Badge key={key} variant="secondary" className="text-[10px]">{KEY_LABELS[key] || key} : {v.count} génération(s) · {usd(v.cost_usd)}</Badge>
            ))}
            {data.failures_this_month > 0 && (
              <Badge className="text-[10px] bg-destructive/10 text-destructive hover:bg-destructive/10" data-testid="ai-usage-failures"><AlertTriangle className="h-3 w-3 mr-1" /> {data.failures_this_month} échec(s) ce mois</Badge>
            )}
          </div>

          {/* Monthly table */}
          <div className="rounded-xl border border-border overflow-x-auto">
            <Table data-testid="ai-usage-table">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Mois</TableHead>
                  <TableHead className="text-xs text-right">Textes</TableHead>
                  <TableHead className="text-xs text-right">Images</TableHead>
                  <TableHead className="text-xs text-right">Vidéos</TableHead>
                  <TableHead className="text-xs text-right">Coût estimé</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[...data.months].reverse().map((row) => (
                  <TableRow key={row.month} data-testid="ai-usage-row" className={row.month === current.month ? 'bg-[hsl(var(--brand-mint))]/20' : ''}>
                    <TableCell className="text-sm font-medium capitalize">{formatMonth(row.month)}</TableCell>
                    <TableCell className="text-sm text-right">{row.texts}</TableCell>
                    <TableCell className="text-sm text-right">{row.images}</TableCell>
                    <TableCell className="text-sm text-right">{row.videos}</TableCell>
                    <TableCell className="text-sm text-right font-medium">{eur(row.cost_total_eur)} <span className="text-[11px] text-muted-foreground">({usd(row.cost_total_usd)})</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <p className="text-[11px] text-muted-foreground" data-testid="ai-usage-note">
            Tarifs unitaires estimés : texte {usd(data.unit_costs.text_default)} (Gemini 3.1 Pro {usd(data.unit_costs.text['gemini-3.1-pro-preview'])}) · image {usd(data.unit_costs.image)} · vidéo {usd(data.unit_costs.video)}. {data.note}
          </p>
        </div>
      )}
    </Card>
  );
}
